import { Schema } from 'effect';

import { DateTimeSerializableFromString } from '../DateTimeSerializableFromString';
import { overrideSchema } from '../openapi/overrideSchema';

/**
 * Branded curated Faq / Banner / Address / FavouriteList schemas. `*Base`
 * exports deliberately stay unbranded so tenants can compose their own
 * serialization contracts before the application applies brands.
 */

/**
 * Override slots whose base schemas intentionally avoid importing a tenant's
 * generated CMS or recursive-reference closure. The tenant/app supplies the
 * exact generated schema via `overrideSchema`; the base intentionally stays
 * loose.
 */
export type MiscOverrideSlots =
  | 'FaqQuestionShow.relatedQuestions'
  | 'GlossaryTermShow.content'
  | 'PageShow.content';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);
const ReadOnlyNullableString = Schema.optionalKey(NullableString.annotate({ readOnly: true }));
const ReadOnlyInt = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
);
const RouterUrl = Schema.String.annotate({
  description: 'Frontend router URL for this resource.',
  readOnly: true,
});

const FaqQuestionDetailBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  priority: Schema.Finite.check(Schema.isInt()),
  question: ReadOnlyNullableString,
  answer: ReadOnlyNullableString,
  slug: NullableString,
  routerUrl: RouterUrl,
});

const FaqQuestionIndexBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  priority: Schema.Finite.check(Schema.isInt()),
  question: ReadOnlyNullableString,
  slug: NullableString,
  routerUrl: RouterUrl,
});

const FaqTopicShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  name: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  slug: NullableString,
  routerUrl: RouterUrl,
});

// ─── Faq ─────────────────────────────────────────────────────────────────────

export const FaqTopicIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  icon: NullableString,
  priority: Schema.Finite.check(Schema.isInt()),
  questions: Schema.Array(FaqQuestionIndexBase),
  name: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  slug: NullableString,
  routerUrl: RouterUrl,
});

export const FaqTopicDetailBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  icon: NullableString,
  questions: Schema.Array(FaqQuestionDetailBase),
  name: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  slug: NullableString,
  routerUrl: RouterUrl,
});

export const FaqQuestionShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  code: Schema.String,
  topic: FaqTopicShowBase,
  question: ReadOnlyNullableString,
  answer: ReadOnlyNullableString,
  slug: NullableString,
  relatedQuestions: Schema.optionalKey(
    /**
     * Override slot: the tenant/app supplies the exact recursive relation
     * schema via `overrideSchema`; the base intentionally stays loose.
     */
    Schema.Array(Schema.Unknown).annotate({
      description: 'First level = FaqQuestion object, deeper levels = IRI. Limited to 6 questions.',
      readOnly: true,
    })
  ),
  routerUrl: RouterUrl,
});

const FaqTopicIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('FaqTopic.iri'))
);
export const FaqTopicCode = Schema.String.pipe(Schema.brand('FaqTopic.code'));
export type FaqTopicCode = typeof FaqTopicCode.Type;
const FaqTopicName = Schema.optionalKey(
  Schema.Union([Schema.String.pipe(Schema.brand('FaqTopic.name')), Schema.Null]).annotate({
    readOnly: true,
  })
);
const FaqTopicSlug = Schema.Union([Schema.String.pipe(Schema.brand('FaqTopic.slug')), Schema.Null]);
const FaqTopicRouterUrl = RouterUrl.pipe(Schema.brand('FaqTopic.routerUrl'));

export const FaqTopicIndex = overrideSchema(FaqTopicIndexBase, {
  '@id': FaqTopicIri,
  code: FaqTopicCode,
  name: FaqTopicName,
  slug: FaqTopicSlug,
  routerUrl: FaqTopicRouterUrl,
}).annotate({ identifier: 'FaqTopicIndex' });
export type FaqTopicIndex = Schema.Schema.Type<typeof FaqTopicIndex>;

export const FaqTopicDetail = overrideSchema(FaqTopicDetailBase, {
  '@id': FaqTopicIri,
  code: FaqTopicCode,
  name: FaqTopicName,
  slug: FaqTopicSlug,
  routerUrl: FaqTopicRouterUrl,
}).annotate({ identifier: 'FaqTopicDetail' });
export type FaqTopicDetail = Schema.Schema.Type<typeof FaqTopicDetail>;

export const FaqQuestionCode = Schema.String.pipe(Schema.brand('FaqQuestion.code'));
export type FaqQuestionCode = typeof FaqQuestionCode.Type;

export const FaqQuestionShow = overrideSchema(FaqQuestionShowBase, {
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('FaqQuestion.iri'))
  ),
  code: FaqQuestionCode,
  slug: Schema.Union([Schema.String.pipe(Schema.brand('FaqQuestion.slug')), Schema.Null]),
  routerUrl: RouterUrl.pipe(Schema.brand('FaqQuestion.routerUrl')),
}).annotate({ identifier: 'FaqQuestionShow' });
export type FaqQuestionShow = Schema.Schema.Type<typeof FaqQuestionShow>;

// ─── Glossary ────────────────────────────────────────────────────────────────

/**
 * The detail read is keyed by `code` while the public URL carries `slug`
 * (`/g/{slug}`); the backend app-router bridges the two, and `routerUrl` is the
 * canonical link to render.
 */
const GlossaryTermIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('GlossaryTerm.iri'))
);
export const GlossaryTermCode = Schema.String.pipe(Schema.brand('GlossaryTerm.code'));
export type GlossaryTermCode = typeof GlossaryTermCode.Type;
const GlossaryTermName = Schema.optionalKey(
  Schema.Union([Schema.String.pipe(Schema.brand('GlossaryTerm.name')), Schema.Null]).annotate({
    readOnly: true,
  })
);
const GlossaryTermSlug = Schema.Union([
  Schema.String.pipe(Schema.brand('GlossaryTerm.slug')),
  Schema.Null,
]);
const GlossaryTermRouterUrl = RouterUrl.pipe(Schema.brand('GlossaryTerm.routerUrl'));

const GlossaryTermId = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true, format: 'uuid' })
);

/** Serialization group `sylius:shop:glossary:index`. */
export const GlossaryTermIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: GlossaryTermId,
  code: Schema.String,
  enabled: Schema.Boolean.annotate({ default: true }),
  favorite: Schema.Boolean.annotate({ default: false }),
  name: ReadOnlyNullableString,
  slug: NullableString,
  tooltip: ReadOnlyNullableString,
  routerUrl: RouterUrl,
});

/** Serialization group `sylius:shop:glossary:show`. */
export const GlossaryTermShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: GlossaryTermId,
  code: Schema.String,
  enabled: Schema.Boolean.annotate({ default: true }),
  channels: Schema.optionalKey(Schema.Array(Schema.String).annotate({ readOnly: true })),
  favorite: Schema.Boolean.annotate({ default: false }),
  name: ReadOnlyNullableString,
  slug: NullableString,
  /**
   * Override slot: the tenant/app supplies the exact CMS element schema via
   * `overrideSchema`; the base intentionally stays loose.
   */
  content: Schema.Array(Schema.Unknown),
  tooltip: ReadOnlyNullableString,
  metaTitle: ReadOnlyNullableString,
  metaDescription: ReadOnlyNullableString,
  metaImage: ReadOnlyNullableString,
  /** Media-cache URL of the meta image; the filter placeholder is resolved by the consumer. */
  metaImageAbstract: ReadOnlyNullableString,
  routerUrl: RouterUrl,
});

export const GlossaryTermIndex = overrideSchema(GlossaryTermIndexBase, {
  '@id': GlossaryTermIri,
  code: GlossaryTermCode,
  name: GlossaryTermName,
  slug: GlossaryTermSlug,
  routerUrl: GlossaryTermRouterUrl,
}).annotate({ identifier: 'GlossaryTermIndex' });
export type GlossaryTermIndex = Schema.Schema.Type<typeof GlossaryTermIndex>;

export const GlossaryTermShow = overrideSchema(GlossaryTermShowBase, {
  '@id': GlossaryTermIri,
  code: GlossaryTermCode,
  name: GlossaryTermName,
  slug: GlossaryTermSlug,
  routerUrl: GlossaryTermRouterUrl,
}).annotate({ identifier: 'GlossaryTermShow' });
export type GlossaryTermShow = Schema.Schema.Type<typeof GlossaryTermShow>;

/**
 * `GET /shop/glossary-terms/alphabet` — the distinct first characters of
 * `GlossaryTermTranslation.name` for the request locale, single-char entries
 * matching the `alphabet` collection filter's domain.
 */
export const GlossaryAlphabet = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  alphabet: Schema.Array(Schema.String),
}).annotate({ identifier: 'GlossaryAlphabet' });
export type GlossaryAlphabet = Schema.Schema.Type<typeof GlossaryAlphabet>;

// ─── Banner ──────────────────────────────────────────────────────────────────

export const BannerBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.optionalKey(Schema.String.annotate({ readOnly: true, format: 'uuid' })),
  code: Schema.String,
  priority: Schema.Finite.check(Schema.isInt()),
  placement: Schema.Literals([
    'homepage',
    'category_listing_1',
    'category_listing_2',
    'category_description',
    'category_footer',
    'product_under_gallery',
    'product_main_info',
    'customer_guide',
  ]),
  imageDesktop: NullableString,
  imageTablet: NullableString,
  imageMobil: NullableString,
  url: NullableString,
  titleColor: Schema.String.annotate({ default: '#FFFFFF' }),
  descriptionColor: Schema.String.annotate({ default: '#FFFFFF' }),
  labelColor: Schema.String.annotate({ default: '#AFD816' }),
  highlightEnabled: Schema.Boolean.annotate({ default: false }),
  highlightColor: Schema.String.annotate({ default: '#000000' }),
  buttonTextColor: Schema.String.annotate({ default: '#FFFFFF' }),
  imageDesktopAbstract: ReadOnlyNullableString,
  imageTabletAbstract: ReadOnlyNullableString,
  imageMobilAbstract: ReadOnlyNullableString,
  title: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  label: ReadOnlyNullableString,
  link: ReadOnlyNullableString,
  buttonText: ReadOnlyNullableString,
  toDate: Schema.optionalKey(Schema.Union([DateTimeSerializableFromString, Schema.Null])),
});

export const BannerTitle = Schema.String.pipe(Schema.brand('Banner.title'));
export const Banner = overrideSchema(BannerBase, {
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Banner.iri'))
  ),
  id: Schema.optionalKey(
    Schema.String.annotate({ readOnly: true, format: 'uuid' }).pipe(Schema.brand('Banner.id'))
  ),
  code: Schema.String.pipe(Schema.brand('Banner.code')),
  title: Schema.optionalKey(Schema.Union([BannerTitle, Schema.Null]).annotate({ readOnly: true })),
}).annotate({ identifier: 'Banner' });
export type Banner = Schema.Schema.Type<typeof Banner>;

// ─── CMS Page ────────────────────────────────────────────────────────────────

export const PageShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: ReadOnlyInt,
  metaImageAbstract: ReadOnlyNullableString,
  title: NullableString,
  slug: NullableString,
  metaTitle: NullableString,
  metaKeywords: NullableString,
  metaDescription: NullableString,
  metaImage: NullableString,
  /**
   * Override slot: the tenant/app supplies the exact CMS element schema via
   * `overrideSchema`; the base intentionally stays loose.
   */
  content: Schema.Array(Schema.Unknown),
  routerUrl: RouterUrl,
});

export const PageId = Schema.Finite.pipe(Schema.brand('Page.id'));
export type PageId = typeof PageId.Type;
export const PageShow = overrideSchema(PageShowBase, {
  id: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt()).pipe(Schema.brand('Page.id'))
  ),
}).annotate({ identifier: 'PageShow' });
export type PageShow = Schema.Schema.Type<typeof PageShow>;

// ─── Address (read side) ─────────────────────────────────────────────────────

export const AddressShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  vatNumber: NullableString,
  businessId: NullableString.annotate({ description: 'IČO or similar ID for other countries' }),
  id: ReadOnlyInt,
  firstName: Schema.String,
  lastName: Schema.String,
  phoneNumber: NullableString,
  company: NullableString,
  countryCode: Schema.String,
  provinceCode: NullableString,
  provinceName: NullableString,
  street: Schema.String,
  city: Schema.String,
  postcode: Schema.String,
});

export const AddressShow = overrideSchema(AddressShowBase, {
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Address.iri'))
  ),
  id: Schema.optionalKey(
    Schema.Finite.annotate({ readOnly: true })
      .check(Schema.isInt())
      .pipe(Schema.brand('Address.id'))
  ),
  firstName: Schema.String.pipe(Schema.brand('Address.firstName')),
  lastName: Schema.String.pipe(Schema.brand('Address.lastName')),
  phoneNumber: Schema.Union([Schema.String.pipe(Schema.brand('Address.phoneNumber')), Schema.Null]),
  street: Schema.String.pipe(Schema.brand('Address.street')),
  city: Schema.String.pipe(Schema.brand('Address.city')),
  postcode: Schema.String.pipe(Schema.brand('Address.postcode')),
}).annotate({ identifier: 'AddressShow' });
export type AddressShow = Schema.Schema.Type<typeof AddressShow>;

// ─── Product favourite lists ─────────────────────────────────────────────────

export const ProductFavouriteListWithoutProductsBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.optionalKey(Schema.String.annotate({ readOnly: true, format: 'uuid' })),
  name: Schema.String,
});

export const ProductFavouriteListWithProductCodesBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.optionalKey(Schema.String.annotate({ readOnly: true, format: 'uuid' })),
  name: Schema.String,
  productVariantCodes: Schema.optionalKey(Schema.Array(Schema.String).annotate({ readOnly: true })),
});

export const ProductFavouriteListCreateResponseBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  uuid: Schema.String,
  name: Schema.String,
  productVariantCodes: Schema.Array(Schema.String),
});

const FavouriteListIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('FavouriteList.iri'))
);
export const FavouriteListId = Schema.String.pipe(Schema.brand('FavouriteList.id'));
const FavouriteListIdField = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true, format: 'uuid' }).pipe(Schema.brand('FavouriteList.id'))
);
export const FavouriteListName = Schema.String.pipe(Schema.brand('FavouriteList.name'));

export const ProductFavouriteListWithoutProducts = overrideSchema(
  ProductFavouriteListWithoutProductsBase,
  {
    '@id': FavouriteListIri,
    id: FavouriteListIdField,
    name: FavouriteListName,
  }
).annotate({ identifier: 'ProductFavouriteListWithoutProducts' });
export type ProductFavouriteListWithoutProducts = Schema.Schema.Type<
  typeof ProductFavouriteListWithoutProducts
>;

export const ProductFavouriteListWithProductCodes = overrideSchema(
  ProductFavouriteListWithProductCodesBase,
  {
    '@id': FavouriteListIri,
    id: FavouriteListIdField,
    name: FavouriteListName,
  }
).annotate({ identifier: 'ProductFavouriteListWithProductCodes' });
export type ProductFavouriteListWithProductCodes = Schema.Schema.Type<
  typeof ProductFavouriteListWithProductCodes
>;

export const ProductFavouriteListCreateResponse = overrideSchema(
  ProductFavouriteListCreateResponseBase,
  {
    '@id': FavouriteListIri,
    uuid: FavouriteListId,
    name: FavouriteListName,
  }
).annotate({ identifier: 'ProductFavouriteListCreateResponse' });
export type ProductFavouriteListCreateResponse = Schema.Schema.Type<
  typeof ProductFavouriteListCreateResponse
>;
